import { Action } from "redux"
import { INotificationMessage, NotificationType } from "./index"

export const INITIALIZE = "NOTIFICATIONS_INITIALIZE"
export const ADD_ERROR_NOTIFICATION = "NOTIFICATIONS_ADD_ERROR_NOTIFICATION"
export const ADD_INFORMATION_NOTIFICATION =
    "NOTIFICATIONS_ADD_INFORMATION_NOTIFICATION"
export const ADD_MESSAGE = "NOTIFICATIONS_ADD_MESSAGE"
export const ADD_MESSAGE_TRIGGER = "NOTIFICATIONS_ADD_MESSAGE_TRIGGER"
export const ADD_WARNING_NOTIFICATION = "NOTIFICATIONS_ADD_WARNING_NOTIFICATION"
export const APPEND_OR_UPDATE_PREVIOUS_MESSAGE =
    "NOTIFICATIONS_APPEND_OR_UPDATE_PREVIOUS_MESSAGE"
export const APPEND_PREVIOUS_MESSAGE = "NOTIFICATIONS_APPEND_PREVIOUS_MESSAGE"
export const APPEND_PREVIOUS_MESSAGE_TRIGGER =
    "NOTIFICATIONS_APPEND_PREVIOUS_MESSAGE_TRIGGER"
export const EXPAND = "NOTIFICATIONS_EXPAND"
export const FLASH = "NOTIFICATIONS_FLASH"
export const OVERWRITE_PREVIOUS_MESSAGE =
    "NOTIFICATIONS_OVERWRITE_PREVIOUS_MESSAGE"
export const OVERWRITE_PREVIOUS_MESSAGE_TRIGGER =
    "NOTIFICATIONS_OVERWRITE_PREVIOUS_MESSAGE_TRIGGER"
export const REMOVE_ALL_MESSAGES = "NOTIFICATIONS_REMOVE_ALL_MESSAGES"
export const REMOVE_MESSAGE = "NOTIFICATIONS_REMOVE_MESSAGE"

export interface NotificationMessage extends Action {
    message: INotificationMessage
}

export interface NotificationMessageId extends Action {
    id: string
}

export interface NotificationMessageTrigger extends Action {
    message: INotificationMessage
    autoRemove?: number
}

export interface NewNotificationMessage extends Action {
    message: string
    attrs: string[]
    autoRemove?: number
    overwriteExisting: boolean
}

export interface NewTypedNotificationMessage extends NewNotificationMessage {
    messageType: NotificationType
}

export interface NotificationMessageFlash extends Action {
    isFlashing: boolean
}

export interface NotificationMessageExpand extends Action {
    id: string
    expanded: boolean
}

export const initialize = (): Action => ({ type: INITIALIZE })

export const addMessage = (
    message: INotificationMessage,
): NotificationMessage => ({ type: ADD_MESSAGE, message })

export const addMessageTrigger = (
    message: INotificationMessage,
    autoRemove: number | undefined,
): NotificationMessageTrigger => ({
    type: ADD_MESSAGE_TRIGGER,
    message,
    autoRemove,
})

export const appendPreviousMessage = (
    message: INotificationMessage,
): NotificationMessage => ({ type: APPEND_PREVIOUS_MESSAGE, message })

export const appendPreviousMessageTrigger = (
    message: INotificationMessage,
    autoRemove: number | undefined,
): NotificationMessageTrigger => ({
    type: APPEND_PREVIOUS_MESSAGE_TRIGGER,
    message,
    autoRemove,
})

export const appendOrUpdatePreviousMessage = (
    message: string,
    messageType: NotificationType,
    overwriteExisting: boolean,
    autoRemove: number | undefined,
    attrs: string[],
): NewTypedNotificationMessage => ({
    type: APPEND_OR_UPDATE_PREVIOUS_MESSAGE,
    message,
    messageType,
    overwriteExisting,
    autoRemove,
    attrs,
})

export const addErrorNotification = (
    message: string,
    overwriteExisting = false,
    autoRemove: number | undefined = undefined,
    attrs: string[] = [],
): NewNotificationMessage => ({
    type: ADD_ERROR_NOTIFICATION,
    message,
    overwriteExisting,
    autoRemove,
    attrs,
})

export const addInformationNotification = (
    message: string,
    overwriteExisting = false,
    autoRemove: number | undefined = undefined,
    attrs: string[] = [],
): NewNotificationMessage => ({
    type: ADD_INFORMATION_NOTIFICATION,
    message,
    overwriteExisting,
    autoRemove,
    attrs,
})

export const addWarningNotification = (
    message: string,
    overwriteExisting = false,
    autoRemove: number | undefined = undefined,
    attrs: string[] = [],
): NewNotificationMessage => ({
    type: ADD_WARNING_NOTIFICATION,
    message,
    overwriteExisting,
    autoRemove,
    attrs,
})

export const expandMessage = (
    id: string,
    expanded: boolean,
): NotificationMessageExpand => ({ type: EXPAND, id, expanded })

export const flash = (isFlashing: boolean): NotificationMessageFlash => ({
    type: FLASH,
    isFlashing,
})

export const overwritePreviousMessage = (
    message: INotificationMessage,
): NotificationMessage => ({ type: OVERWRITE_PREVIOUS_MESSAGE, message })

export const overwritePreviousMessageTrigger = (
    message: INotificationMessage,
    autoRemove: number | undefined,
): NotificationMessageTrigger => ({
    type: OVERWRITE_PREVIOUS_MESSAGE_TRIGGER,
    message,
    autoRemove,
})

export const removeAllMessages = (): Action => ({ type: REMOVE_ALL_MESSAGES })

export const removeMessage = (
    message: INotificationMessage,
): NotificationMessage => ({ type: REMOVE_MESSAGE, message })

export const mapDispatchToProps = {
    initialize,
    expandMessage,
    flash,
    removeAllMessages,
    removeMessage,
    appendOrUpdatePreviousMessage,
    addErrorNotification,
    addInformationNotification,
    addWarningNotification,
}

export type DispatchProps = typeof mapDispatchToProps
